import { useState, useMemo, useRef, useEffect } from 'react'
import { useScopeStore } from '../../store/scopeStore'
import { useFleet, statusDot } from '../../hooks/useFleet'
import { Sym } from '../ui/Sym'

export default function WorkspaceSwitcher() {
  const { scope, setScope } = useScopeStore()
  const { rows } = useFleet()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const isFleet = scope === 'fleet'
  const current = rows.find((r) => r.id === scope)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? rows.filter((r) => r.name.toLowerCase().includes(q) || r.domain.toLowerCase().includes(q)) : rows
  }, [rows, query])

  const pick = (s: string) => { setScope(s); setOpen(false); setQuery('') }

  return (
    <div className="px-3 pb-3.5 relative" ref={ref}>
      <div className="font-mono text-[9.5px] tracking-[1.5px] text-text-faint mx-1 mb-1.5 mt-0.5">WORKSPACE</div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 bg-surface border border-border rounded-[10px] px-2.5 py-2 hover:border-border transition-colors text-left"
      >
        <div className="w-7 h-7 rounded-[7px] flex-shrink-0 flex items-center justify-center text-text-primary" style={{ background: isFleet ? 'linear-gradient(135deg,#3b82f6,#2563eb)' : '#1d4ed8' }}>
          <Sym name={isFleet ? 'hub' : 'apartment'} size={16} />
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <div className="text-[13px] font-semibold truncate">{isFleet ? 'All Tenants' : current?.name ?? 'Tenant'}</div>
          <div className="text-[10.5px] text-text-faint truncate">{isFleet ? `${rows.length} tenant${rows.length === 1 ? '' : 's'} monitored` : current?.domain ?? ''}</div>
        </div>
        <Sym name="unfold_more" size={18} className="text-text-faint" />
      </button>

      {open && (
        <div className="absolute left-3 right-3 top-full mt-1.5 bg-surface border border-border rounded-xl p-1.5 z-[60] shadow-2xl max-h-[380px] overflow-y-auto">
          <div className="flex items-center gap-2 bg-surface-raised border border-border rounded-lg px-2.5 py-1.5 mb-1.5">
            <Sym name="search" size={16} className="text-text-faint" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter tenants…"
              className="flex-1 bg-transparent text-xs text-text-primary placeholder:text-text-faint outline-none"
            />
          </div>
          <button
            onClick={() => pick('fleet')}
            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-surface-raised ${isFleet ? 'bg-surface-raised' : ''}`}
          >
            <span className="w-2 h-2 rounded-full flex-shrink-0 bg-primary-500" />
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-medium truncate">All Tenants</div>
              <div className="text-[10.5px] text-text-faint font-mono truncate">Aggregate · {rows.length} tenant{rows.length === 1 ? '' : 's'}</div>
            </div>
            {isFleet && <Sym name="check" size={17} className="text-primary-400" />}
          </button>
          {filtered.map((r) => (
            <button
              key={r.id}
              onClick={() => pick(r.id)}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-surface-raised ${scope === r.id ? 'bg-surface-raised' : ''}`}
            >
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot(r.status)}`} />
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-medium truncate">{r.name}</div>
                <div className="text-[10.5px] text-text-faint font-mono truncate">{r.domain}</div>
              </div>
              {scope === r.id && <Sym name="check" size={17} className="text-primary-400" />}
            </button>
          ))}
          {filtered.length === 0 && <div className="px-2.5 py-3 text-xs text-text-faint text-center">No tenants match.</div>}
        </div>
      )}
    </div>
  )
}
